import type { CopilotProposalSummary } from "@repo/app-types/entities/copilotProposal";
import type { TrainingOperationCounts } from "@repo/app-types/entities/trainingProposal";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import type { CopilotProposalCardContent } from "../../../researchAgent/copilotProposalCard.js";
import {
  buildTrainingCreateLocation,
  buildTrainingProposalEditLocation,
} from "./trainingEditorUrlState.js";

/** What a training proposal's card in the chat shows and opens. */
export function readTrainingProposalCardContent(
  proposal: CopilotProposalSummary<"training">,
): CopilotProposalCardContent {
  const highlights = buildTrainingProposalHighlights(proposal.operation_counts);
  const resourceLocation = {
    pathname: buildProjectUrlPath(
      proposal.resource.id,
      ProjectDetailsTabPath.ProjectConfiguration,
    ),
  };

  switch (proposal.kind) {
    case "create_training":
      return {
        resourceType: "training",
        operation: "create",
        summary: proposal.data.summary,
        highlights,
        // A new training has no project until the user creates it, so the card
        // opens the trainings list, where the modal takes the proposal.
        review: {
          kind: "open_editor",
          location: buildTrainingCreateLocation({ proposalId: proposal.id }),
        },
        resourceLocation,
      };
    case "update_training":
      return {
        resourceType: "training",
        operation: "update",
        summary: proposal.data.summary,
        highlights,
        review: {
          kind: "open_editor",
          location: buildTrainingProposalEditLocation({
            projectId: proposal.resource.id,
            proposalId: proposal.id,
          }),
        },
        resourceLocation,
      };
  }
}

/**
 * The card's highlights: what the batch does, counted. The builder's tree
 * badges show additions and edits once the proposal is in the draft, but a
 * removal leaves nothing to badge, so the count is where the user sees it.
 * Reordering changes nothing about what the training teaches, so it is left
 * to the summary.
 */
function buildTrainingProposalHighlights(
  counts: TrainingOperationCounts,
): readonly string[] {
  return [
    buildCountHighlight(counts.chapters.added, "chapter", "added"),
    buildCountHighlight(counts.chapters.updated, "chapter", "renamed"),
    buildCountHighlight(counts.chapters.removed, "chapter", "removed"),
    buildCountHighlight(counts.lessons.added, "lesson", "added"),
    buildCountHighlight(counts.lessons.updated, "lesson", "updated"),
    buildCountHighlight(counts.lessons.removed, "lesson", "removed"),
  ].filter((highlight) => highlight !== undefined);
}

function buildCountHighlight(
  count: number,
  noun: string,
  verb: string,
): string | undefined {
  if (count === 0) {
    return undefined;
  }

  return `${count} ${count === 1 ? noun : `${noun}s`} ${verb}`;
}
